import { useMemo } from 'react';
import { Droppable } from 'react-beautiful-dnd';
import { Wrapper } from '@screentone/core';

import { CommonEntityProps } from 'components/datamodel/commonEntityProps';
// eslint-disable-next-line import/no-cycle -- TODO: pre-existing cycle via DataModelContext, fix when context is restructured
import { useDataModelContext } from 'contexts/datamodel/useDataModel';
import { DROPPABLE_ID_PREFIXES, DROPPABLE_TYPES } from 'contexts/drag-and-drop/dragUtils';
// eslint-disable-next-line import/no-cycle -- TODO: pre-existing cycle via DataModelContext, fix when context is restructured
import { usePageTypeSettingsContext } from 'contexts/page-type-settings/usePageTypeSettings';
import { PageContainer } from 'data/generated/graphql';
import styles from './Page.module.scss';
import TopOptionalToggle from '../Module/components/top-optional-toggle/TopOptionalToggle';

import { EntityType } from 'contexts/datamodel/DataModelContext.constants';

interface PageProps extends CommonEntityProps {
  data: PageContainer;
}

export const Page = ({ data: page, parentHierarchyId, index, isHistory, isReadOnly }: PageProps) => {
  const { generateHierarchyId, renderEntity, insertEntity, removeEntity } = useDataModelContext();
  const { availableRequiredLayoutModules, currentLayout } = usePageTypeSettingsContext();

  const hierarchyId = generateHierarchyId(page, parentHierarchyId, index);

  const hasGroupAsChildren = useMemo(
    () => page.collection.some((item) => item.type === EntityType.Group),
    [page.collection]
  );
  const padding = hasGroupAsChildren ? {} : { top: 'sm', right: 'md', bottom: 'md', left: 'md' };

  const childrenElements = page.collection.map((collection, i) => (
    <div key={i}>
      {renderEntity(collection, {
        hierarchyId,
        index: i,
        isHistory,
        extraProps: {
          group: {
            groupsItemCount: page.collection
              .filter((item) => item.type === EntityType.Group)
              .map((item) => item.collection.length)
          },
          module: {
            layout: currentLayout
          }
        },
        withoutDragNDrop: !hasGroupAsChildren && page.collection.length === 1,
        isReadOnly
      })}
    </div>
  ));

  return (
    <Wrapper padding={padding} className={styles.pageWrapper} data-model-hierarchy-id={hierarchyId}>
      {!isHistory && (
        <TopOptionalToggle
          page={page}
          availableRequiredLayoutModules={availableRequiredLayoutModules}
          insertEntity={insertEntity}
          removeEntity={removeEntity}
          isReadOnly={isReadOnly}
        />
      )}
      {!hasGroupAsChildren && <Wrapper className={styles.pageBackground} />}

      {!isHistory && page.collection.length > 1 ? (
        /* Wrap children in Droppable only for draft section with multiple modules */
        <Droppable droppableId={DROPPABLE_ID_PREFIXES.MODULES + hierarchyId} type={DROPPABLE_TYPES.MODULE}>
          {(droppableProvided) => (
            <div
              ref={droppableProvided.innerRef}
              {...droppableProvided.droppableProps}
              data-testid="page-draft-modules"
            >
              {childrenElements}
              {droppableProvided.placeholder}
            </div>
          )}
        </Droppable>
      ) : (
        childrenElements
      )}
    </Wrapper>
  );
};
